/* eslint-disable react/function-component-definition */
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faEdit } from "@fortawesome/fontawesome-free-solid";
import { Entry } from "../../redux/entrySlice";
import TextInput from "../../components/TextField/TextField";
import SelectBox from "../../components/SelectBox/SelectBox";
import styles from "./editAppVersionPopup.module.scss";
import "react-toastify/dist/ReactToastify.css";
import Loader from '../../components/Loader/Loader';

const appTypeOptions = [
  { label: "Customer", value: "customer" },
  { label: "Vendor", value: "vendor" },
];

const platformOptions = [
  { label: "iOS", value: "ios" },
  { label: "Android", value: "android" },
];

const EditAppVersionPopup = React.memo(
  ({ appVersionId, handleClose, setIsUpdated, edit = true }) => {
    const dispatch = useDispatch();
    const {
      control,
      register,
      handleSubmit,
      formState: { errors },
      reset,
    } = useForm();

    const [appVersion, setAppVersion] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [isEditing, setIsEditing] = useState(false);

    useEffect(() => {
      const actionData = {
        actionUrl: `appversions/${appVersionId}`,
        actionMethod: "get",
      };

      dispatch(Entry(actionData)).then((resp) => {
        if (resp.payload.data.appVersion) {
          setAppVersion(resp.payload.data.appVersion);
          reset(resp.payload.data.appVersion);
        }
      });
    }, [appVersionId, dispatch, reset]);

    const onSubmitData = (data) => {
      setSubmitting(true);
      setError(null);
      const apiData = {
        appType: data.appType,
        platform: data.platform,
        version: data.version,
        buildversion: data.buildversion,
        isForceUpdate: data.isForceUpdate || false,
      };
      data.actionUrl = `appversions/${appVersionId}`;
      data.actionMethod = "patch";
      data.apiData = apiData;

      dispatch(Entry(data)).then((resp) => {
        setSubmitting(false);
        if (resp.payload.code === 200) {
          toast.success("App Version updated successfully");
          setIsEditing(false);
          handleClose();
          setIsUpdated(true);
        } else {
          setError(resp.payload.message);
          toast.error(resp.payload.message);
        }
      });
    };

    const toggleEditMode = () => {
      setIsEditing(!isEditing);
    };

    return (
      <div className={styles.popupbox}>
        <div className={styles.box}>
          {/* Header */}
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="d-flex align-items-center gap-3">
              <h2 className={styles.heading}>
                {isEditing ? "Edit App Version" : "App Version Details"}
              </h2>
              {!isEditing && edit && (
                <button
                  type="button"
                  className={`${styles.formbtnedit} d-flex align-items-center`}
                  onClick={toggleEditMode}
                >
                  <FontAwesomeIcon
                    icon={faEdit}
                    style={{ color: "white", marginRight: "5px" }}
                  />
                  <span style={{ color: "white" }}>Edit</span>
                </button>
              )}
            </div>
            <button
              className={styles.closeicon}
              aria-label="Close"
              onClick={handleClose}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          {/* Form */}
          <div className="container-fluid">
            {appVersion ? (
              <form onSubmit={handleSubmit(onSubmitData)}>
                <div className="row">
                  <div className="col-md-6">
                    <SelectBox
                      className={styles.inputbox}
                      labelstyle={styles.formlabel}
                      label="App Type"
                      name="appType"
                      control={control}
                      register={register}
                      values={appTypeOptions}
                      errors={errors}
                      placeholder="Choose App Type"
                      defaultvalue={appVersion.appType}
                      mandatory
                      Disabled
                    />
                  </div>
                  <div className="col-md-6">
                    <SelectBox
                      className={styles.inputbox}
                      labelstyle={styles.formlabel}
                      label="Platform"
                      name="platform"
                      control={control}
                      register={register}
                      values={platformOptions}
                      errors={errors}
                      placeholder="Choose Platform"
                      defaultvalue={appVersion.platform}
                      mandatory
                      Disabled
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <TextInput
                      className={styles.inputbox}
                      label="Version"
                      fieldName="version"
                      placeHolder="Enter Version (e.g. 1.0.0)"
                      register={register}
                      errors={errors}
                      isRequired
                      mandatory
                      maximLength={20}
                      defaultValue={appVersion.version}
                      readOnly={!isEditing}
                    />
                  </div>
                  <div className="col-md-6">
                    <TextInput
                      className={styles.inputbox}
                      label="Build Version"
                      fieldName="buildversion"
                      placeHolder="Enter Build Version (e.g. 100)"
                      register={register}
                      errors={errors}
                      isRequired
                      mandatory
                      maximLength={20}
                      defaultValue={appVersion.buildversion}
                      readOnly={!isEditing}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6 d-flex align-items-center mt-4">
                    <input
                      type="checkbox"
                      style={{ marginRight: "8px" }}
                      {...register("isForceUpdate")}
                      disabled={!isEditing}
                    />
                    <label className={styles.formlabel}>Force Update</label>
                  </div>
                </div>

                {/* Update Button */}
                {isEditing && (
                  <div className="row mt-3">
                    <div className="col text-center">
                      <input
                        className={styles.formbtn}
                        type="submit"
                        disabled={submitting}
                        value={submitting ? "Please wait.." : "Update"}
                      />
                    </div>
                  </div>
                )}

                {error && <h6 className={styles.errormsg}>{error}</h6>}
              </form>
            ) : (
              <div
                style={{
                  textAlign: "center",
                  marginTop: "130px",
                  color: "#39979d",
                }}
              >
                <Loader />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
);

export default EditAppVersionPopup;
